/**
 * C0 / C0b DE COEXISTENCE · EL LANZAMIENTO DE EMBEDDED SIGNUP Y EL DESENLACE DEL FLUJO, DECIDIDOS EN UN SOLO SITIO
 * (COEXISTENCE-DISENO.md §1 y §6; decisiones de Rodrigo del 21, 23 y 26-oct-2026).
 *
 * LANZAMIENTO POR DEFECTO (C0b, 26-oct-2026): `{ version: 'v5', features: [{ name: 'whatsapp_business_app_onboarding',
 * enabled: false }] }` para TODAS las organizaciones. Por qué: desde el 20-oct-2026 Meta sirve v5 con el onboarding de
 * la app WhatsApp Business ACTIVADO POR DEFECTO, y una petición sin `version` se sirve «como v4» sólo hasta una fecha
 * que Meta no publica («TBD»). Medido el 26-oct con el número de prueba: `{ setup: {} }` (v4) y esta forma (`v5-sin`)
 * muestran el número de la app como «No cumple los requisitos»; `{ version: 'v5' }` lo muestra seleccionable y el
 * alta termina en `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING` SIN `waba_id`, que nuestro callback de hoy no sabe
 * tratar (haría `/register` sobre un número registrado en la app). Fijar v5 con la función apagada quita la fecha
 * abierta del cambio de default de Meta y deja la forma del payload (v5) como la única que el listener lee.
 *
 * Dos llaves para el DESCUBRIMIENTO, y las dos tienen que estar:
 *   1. la organización está en `ORGS_C0` (lista FIJA en el código; hoy sólo Prueba brokers);
 *   2. la URL lleva `?coexistencia=<variante>` con una variante conocida.
 * Con cualquier otra organización, sin el parámetro o con una variante desconocida, el lanzamiento es el de arriba y
 * sin modo descubrimiento. `v5-sin` es idéntica al default y se mantiene como control.
 *
 * MODO DESCUBRIMIENTO (`descubrimiento: true`): el componente NO manda el `code` al callback (ni `/register`, ni
 * `subscribed_apps`, ni escrituras en Firestore) y enseña en pantalla lo que devolvió el flujo. Lo que se enseña lo
 * compone `textoDeDescubrimiento`, que recibe si llegó el `code` como booleano: el `code` mismo no entra aquí nunca.
 *
 * DESENLACE (`desenlaceDelEvento`, regla d74 de CLAUDE.md): la decisión «¿qué hace el listener con un postMessage de
 * `WA_EMBEDDED_SIGNUP`?» es una función pura con fixture real, no una cadena de `if` en el componente. El payload de
 * v5 (medido el 26-oct, dos veces, idéntico) es `{ data: { phone_number_id, messaging_account_ids[], is_on_biz_app,
 * business_id, … }, type, event }`: NO trae `waba_id`; la WABA es `messaging_account_ids[0]`.
 *
 * Es CommonJS, como `cuentaDelChat.js`: se prueba desde `functions/__tests__/` (un test de `src/` no corre en el
 * pre-commit). Todo lo que devuelve `extrasDeLanzamiento` va CONGELADO (H4 de la revisión acotada de 6eed6d5): un
 * `lanzamiento.descubrimiento = false` escrito a mano después de leerlo no puede cambiar nada.
 */

const ORGS_C0 = Object.freeze({
  yTbOOxhOeoWCIELXU82i: 'Prueba brokers',
});

/**
 * C5/C9 (27/28-oct-2026): coexistence está abierto para TODAS las organizaciones (modo organización), gobernado por el
 * interruptor global `configGlobal/coexistence { habilitada }` que el componente lee por `onSnapshot` (el callback lee el
 * mismo documento: `functions/integrations/coexistence/interruptor.js`). Encendido → el lanzamiento es `{ version: 'v5' }`
 * con el onboarding de la app Business ENCENDIDO (medido el 26-oct: la única forma que muestra el número de la app como
 * seleccionable) y el listener SÍ manda el `code` del alta de coexistence. Apagado (sólo `habilitada === false`) → el
 * lanzamiento de C0b (app Business apagada) y el rechazo de siempre. Un número NORMAL sigue el alta normal en los dos casos.
 */
const RUTA_INTERRUPTOR_COEXISTENCE = 'configGlobal/coexistence';

/** La decisión del interruptor, pura y la misma que en el callback: sólo un `false` explícito apaga. */
function interruptorEncendido(datos) {
  return !(datos && datos.habilitada === false);
}

const PARAMETRO = 'coexistencia';

const FUNCION_APP_BUSINESS = 'whatsapp_business_app_onboarding';

/** Congela en profundidad (objetos y arrays). Devuelve el mismo objeto. */
function congelar(x) {
  if (x && typeof x === 'object' && !Object.isFrozen(x)) {
    Object.freeze(x);
    for (const k of Object.keys(x)) congelar(x[k]);
  }
  return x;
}

/**
 * Copia MUTABLE y profunda de un `extras` congelado, para entregársela al SDK de Facebook (🟡2 de la revisión de
 * d39d32d): lo que nosotros guardamos y enseñamos va congelado (H4), pero nadie ha medido si `FB.login` escribe en
 * `extras`; si lo hiciera sobre un objeto congelado, lanzaría dentro de `launchEmbeddedSignup` y el botón quedaría en
 * «Conectando…» para todas las orgs. El SDK recibe siempre un objeto suyo.
 */
function copiaMutable(extras) {
  return JSON.parse(JSON.stringify(extras ?? {}));
}

/** El lanzamiento de todas las orgs: v5 con la app Business APAGADA. Objeto nuevo y congelado en cada llamada. */
function extrasPorDefecto() {
  return congelar({ version: 'v5', features: [{ name: FUNCION_APP_BUSINESS, enabled: false }] });
}

const VARIANTES = Object.freeze({
  'v4-featureType': () => congelar({ setup: {}, featureType: FUNCION_APP_BUSINESS }),
  v5: () => congelar({ version: 'v5' }),
  'v5-sin': () => extrasPorDefecto(),
});

/** Los desenlaces que cierran el flujo (página Implementation, 23-oct-2026); en descubrimiento, cualquiera de ellos para el popup. */
const EVENTOS_TERMINALES = Object.freeze([
  'FINISH',
  'FINISH_ONLY_WABA',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
  'FINISH_OBO_MIGRATION',
  'FINISH_GRANT_ONLY_API_ACCESS',
  'ERROR',
  'CANCEL',
]);

const EVENTO_COEXISTENCE = 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';

/**
 * @param {{orgId?: string|null, search?: string}} p  `orgId`: la org del contexto (sólo en modo organización);
 *   `search`: `window.location.search` (con o sin `?`).
 * @returns {Readonly<{extras: object, variante: string|null, descubrimiento: boolean}>}  congelado en profundidad
 */
function extrasDeLanzamiento({ orgId = null, search = '', coexistenceHabilitada = true } = {}) {
  // C5/C9: cualquier organización, con el interruptor encendido, lanza v5 con la app Business encendida, sin parámetro ni
  // descubrimiento. El descubrimiento (C0, con parámetro) sigue teniendo prioridad para las orgs de ORGS_C0.
  const porDefecto = () => (!!orgId && coexistenceHabilitada !== false
    ? congelar({ extras: congelar({ version: 'v5' }), variante: 'coexistence', descubrimiento: false, coexistence: true })
    : congelar({ extras: extrasPorDefecto(), variante: null, descubrimiento: false }));
  if (!orgId || !Object.prototype.hasOwnProperty.call(ORGS_C0, orgId)) return porDefecto();
  let variante = null;
  try {
    variante = new URLSearchParams(String(search || '')).get(PARAMETRO);
  } catch (_) {
    return porDefecto();
  }
  if (!variante || !Object.prototype.hasOwnProperty.call(VARIANTES, variante)) return porDefecto();
  return congelar({ extras: VARIANTES[variante](), variante, descubrimiento: true });
}

/**
 * Qué hace el listener con un `postMessage` de tipo `WA_EMBEDDED_SIGNUP` (ya filtrado por origen y tipo). Puro.
 *
 * Orden de las ramas, y por qué:
 *   1. `coexistence`: el evento es `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING` o `data.is_on_biz_app === true`. Va
 *      PRIMERO porque es la única rama que, de caer en otra, haría daño: con `phone_number_id` y una WABA en
 *      `messaging_account_ids` la rama `fijar` mandaría el `code` al callback y éste haría `/register` sobre un
 *      número registrado en la app. Hasta C5 ese alta no se envía.
 *   2. `sin_numero`: `FINISH_ONLY_WABA`, o `FINISH` sin `phone_number_id` (v4 lo admite).
 *   3. `error`: `ERROR` de Meta.
 *   4. `fijar`: hay número y WABA → `signupData` para el callback. La WABA es `waba_id` (v4) o, si no viene,
 *      `messaging_account_ids[0]` (v5). `cuentas` dice cuántos ids trajo: si son más de uno hoy no sabemos cuál es
 *      el del número; se sigue con el primero y el componente lo deja en el log.
 *   5. `ignorar`: cualquier otro mensaje (p. ej. `CANCEL`, o un FINISH a medias); el temporizador del componente
 *      sigue mandando.
 *
 * @param {object} data  el mensaje tal cual (`{ type, event, data: {…}, version? }`)
 * @returns {Readonly<{tipo: 'coexistence'|'sin_numero'|'error'|'fijar'|'ignorar', evento: string|null,
 *   phoneId: string|null, wabaId: string|null, version: string|number|null, cuentas: number, enApp: boolean}>}
 */
function desenlaceDelEvento(data) {
  const d = (data && typeof data === 'object' && data.data && typeof data.data === 'object') ? data.data : {};
  const evento = (data && data.event) || null;
  const phoneId = d.phone_number_id || null;
  const ids = Array.isArray(d.messaging_account_ids) ? d.messaging_account_ids.filter(Boolean) : [];
  const wabaId = d.waba_id || ids[0] || null;
  const version = (data && (data.version ?? d.version)) ?? null;
  // Falla CERRADO: Meta manda un booleano, pero si algún día llegara `'true'` como cadena, tratarlo como «no está en
  // la app» mandaría el `code` al callback. Cualquier otra cosa (false, undefined, null, 0, '') es «no».
  const enApp = d.is_on_biz_app === true || d.is_on_biz_app === 'true';
  const base = { evento, phoneId, wabaId, version, cuentas: ids.length, enApp };
  let tipo;
  if (evento === EVENTO_COEXISTENCE || enApp) tipo = 'coexistence';
  else if (evento === 'FINISH_ONLY_WABA' || (evento === 'FINISH' && !phoneId)) tipo = 'sin_numero';
  else if (evento === 'ERROR') tipo = 'error';
  else if (phoneId && wabaId) tipo = 'fijar';
  else tipo = 'ignorar';
  return congelar({ tipo, ...base });
}

/**
 * El texto del panel de descubrimiento. `codeLlego` es `true | false | null` (null = `FB.login` aún no contestó).
 * `eventos` son los `postMessage` de tipo `WA_EMBEDDED_SIGNUP` tal cual (ids de Meta, nunca el `code`).
 */
function textoDeDescubrimiento({ variante, extras, codeLlego, eventos } = {}) {
  const code = codeLlego === null || codeLlego === undefined ? 'todavía no contestó' : codeLlego ? 'sí' : 'no';
  const lista = Array.isArray(eventos) ? eventos : [];
  const lineas = [
    `MODO DESCUBRIMIENTO (C0) · variante ${variante || '-'} · nada se envía al servidor`,
    `extras: ${JSON.stringify(extras || {})}`,
    `FB.login trajo code: ${code}`,
    `eventos del flujo (${lista.length}):`,
    ...lista.map((e, i) => `${i + 1}. ${JSON.stringify(e)}`),
  ];
  return lineas.join('\n');
}

module.exports = {
  ORGS_C0, RUTA_INTERRUPTOR_COEXISTENCE, interruptorEncendido, PARAMETRO, VARIANTES, EVENTOS_TERMINALES, EVENTO_COEXISTENCE, FUNCION_APP_BUSINESS,
  extrasPorDefecto, extrasDeLanzamiento, copiaMutable, desenlaceDelEvento, textoDeDescubrimiento,
};
